import clsx from 'clsx';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import uniq from 'lodash/uniq';
import { OrderStatus } from '../../../XV2';
import './OrderPlacedOverlay.scss';
import { useInterval } from '../../../hooks/interval';
import { UiImage, UiText, UiIconMaterial } from '@open-stack/ui';
import { OrderProductStatus, OrderProduct } from '../../../XV2';
import { useRouter } from '../../../hooks/router';
import { useOnResume } from '../../../hooks/on-resume';
import { useCustomerOrders } from '../../../hooks/customer-orders';
import Draggable from 'react-draggable';
import { useAppCache, AppCache, useAppCacheAutoSync } from '../../../services/app-cache';
import { OrderIdPage } from '../../../pages/order/id/OrderIdPage';
import debounce from 'lodash/debounce';
import { awsIotSubscription } from '../../../services/aws-iot/subscription';

export function OrderPlacedOverlay() {
    const customerOrders = useCustomerOrders();
    const { push, replaceParams } = useRouter();
    const [isCompact, setIsCompact] = useState(AppCache.get('OrderPlacedOverlay_isCompact') ?? false);
    const [coordinates, setCoordinates] = useAppCache('OrderPlacedOverlay_coordinates');
    const nodeRef = useRef(null);
    const [dragTime, setDragTime] = useState(0);
    const toggleIsCompact = useRef(debounce(() => setIsCompact((isCompact) => !isCompact), 100)).current;

    useAppCacheAutoSync('OrderPlacedOverlay_isCompact', isCompact);

    const updateOrders = useCallback(customerOrders.loadOrders, []);

    useEffect(() => {
        updateOrders();
    }, [updateOrders]);
    useInterval(updateOrders, 30000, [updateOrders]);
    useOnResume(updateOrders, [updateOrders]);

    function getOrderProductStatuses(orderProducts: Pick<OrderProduct, 'status'>[]): OrderProductStatus[] {
        return uniq(
            orderProducts.filter((item) => item.status !== OrderProductStatus.CANCELLED).map(({ status }) => status),
        );
    }

    const orderCount = customerOrders.orders?.length || 0;

    if (!orderCount) {
        return <div />;
    }

    if (!customerOrders.isBubblesVisible) {
        return null;
    }

    return (
        <>
            <Draggable
                nodeRef={nodeRef}
                defaultPosition={coordinates}
                onStart={() => {
                    setDragTime(Date.now());
                }}
                onStop={(event: any, { x, y }) => {
                    const totalDragTimeMilliseconds = Date.now() - dragTime;

                    if (totalDragTimeMilliseconds < 200) {
                        const elements: HTMLElement[] = [
                            event.target,
                            event.target.parentElement,
                            event.target.parentElement.parentElement,
                            event.target.parentElement.parentElement.parentElement,
                            event.target.parentElement.parentElement.parentElement.parentElement,
                        ];

                        const element = elements.find((element) => element?.dataset?.id);

                        if (element) {
                            const order = customerOrders.orders.find(
                                (order) => order.id === Number(element.dataset.id),
                            );

                            if (order) {
                                push(replaceParams(OrderIdPage.route, { orderId: order.id }));
                            }
                        } else {
                            toggleIsCompact();
                        }
                    } else {
                        setCoordinates({ x, y });
                    }
                }}>
                <div ref={nodeRef} className={clsx('OrderPlacedOverlay')}>
                    {orderCount > 1 && (
                        <div className="toggle-compact">
                            <UiIconMaterial>{isCompact ? 'unfold_more' : 'unfold_less'}</UiIconMaterial>
                        </div>
                    )}

                    {customerOrders.orders.map((order) => {
                        const { id, status, sequence, image, orderProducts } = order;

                        const orderProductStatuses = getOrderProductStatuses(orderProducts);

                        const isAttentionRequired =
                            [OrderStatus.REJECTED].includes(status) ||
                            orderProductStatuses.includes(OrderProductStatus.READY);

                        const allProductsDelivered =
                            orderProductStatuses.length === 1 &&
                            orderProductStatuses[0] === OrderProductStatus.DELIVERED;

                        const orderProductStatusClasses = orderProductStatuses.filter(
                            (status) => ![OrderProductStatus.DELIVERED].includes(status),
                        );

                        return (
                            <div key={id} className={clsx('bubble-container', { compact: isCompact })}>
                                <div
                                    className={clsx(
                                        'item',
                                        { allProductsDelivered },
                                        `order-status-${status}`,
                                        isCompact && 'compact',
                                        ...orderProductStatusClasses,
                                        isAttentionRequired && 'animate__animated animate__headShake',
                                    )}
                                    data-id={id}>
                                    <div className="indicator" />

                                    <div className="content">
                                        <UiImage src={image} type="cover" />
                                        {/*<UiImage src={order.outlet?.location.imageLogo} type="cover" />*/}

                                        <div className="overlay">
                                            <UiText weight={3} fontSize={5}>
                                                {sequence}
                                            </UiText>
                                        </div>
                                    </div>

                                    <OrderListener orderId={id} callback={updateOrders} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Draggable>
        </>
    );
}

function OrderListener({ orderId, callback }) {
    awsIotSubscription.useOrderUpdate(orderId, callback);
    return null;
}
